/**
 * TabHandler
 *
 * Handles tab switching with mutual exclusivity and URL hash management.
 * Uses the same pattern as TriangleNav tabs - pure custom implementation
 * without Jupiter's Toggler.
 */

export default class TabHandler {
  constructor(app, tabContainerElement) {
    this.app = app
    this.container = tabContainerElement
    this.triggers = []
    this.contents = []
    this.init()
  }

  init() {
    // Find all tab triggers and content areas in this container
    this.triggers = Array.from(
      this.container.querySelectorAll('[data-tab-trigger]')
    )
    this.contents = Array.from(
      this.container.querySelectorAll('[data-tab-content]')
    )

    if (this.triggers.length === 0) {
      console.warn('No tab triggers found in container', this.container)
      return
    }

    // Set up click handlers
    this.setupClickHandlers()

    // Handle hash on initial page load
    this.handleHash()

    // Listen for hash changes (browser back/forward)
    window.addEventListener('hashchange', () => {
      this.handleHash()
    })
  }

  /**
   * Set up click handlers on tab triggers (TriangleNav pattern)
   */
  setupClickHandlers() {
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const slug = trigger.getAttribute('data-tab-trigger')

        // Find corresponding content
        const content = this.container.querySelector(
          `[data-tab-content="${slug}"]`
        )

        if (!content) {
          console.warn(`No content found for tab trigger: ${slug}`)
          return
        }

        // Update trigger states (like TriangleNav)
        this.triggers.forEach(t => {
          t.classList.remove('is-active')
          t.setAttribute('aria-pressed', 'false')
        })
        trigger.classList.add('is-active')
        trigger.setAttribute('aria-pressed', 'true')

        // Update content visibility
        this.contents.forEach(c => c.classList.add('is-hidden'))
        content.classList.remove('is-hidden')

        // Update URL hash
        const newUrl = `${window.location.pathname}${window.location.search}#${slug}`
        history.pushState(null, '', newUrl)

        console.log('[TabHandler] Switched to tab:', slug)
      })
    })
  }

  /**
   * Parse and handle the current URL hash
   */
  handleHash() {
    const hash = window.location.hash

    // No hash = do nothing (first tab is already visible by default)
    if (!hash || hash === '#') {
      return
    }

    // Remove the # prefix
    const slug = hash.substring(1)

    if (!slug) {
      return
    }

    // Find the trigger with matching data-tab-trigger
    const targetTrigger = this.triggers.find(
      trigger => trigger.getAttribute('data-tab-trigger') === slug
    )

    if (!targetTrigger) {
      // Hash doesn't match any tab in this container - ignore
      return
    }

    // Find the corresponding content area
    const content = this.container.querySelector(
      `[data-tab-content="${slug}"]`
    )

    if (!content) {
      console.warn(`No content found for hash: ${slug}`)
      return
    }

    // Update trigger states
    this.triggers.forEach(t => {
      t.classList.remove('is-active')
      t.setAttribute('aria-pressed', 'false')
    })
    targetTrigger.classList.add('is-active')
    targetTrigger.setAttribute('aria-pressed', 'true')

    // Update content visibility
    this.contents.forEach(c => c.classList.add('is-hidden'))
    content.classList.remove('is-hidden')

    // Scroll to the container
    this.scrollToContainer()

    console.log('[TabHandler] Opened tab from hash:', slug)
  }

  /**
   * Scroll to the tab container accounting for fixed header
   */
  scrollToContainer() {
    // Small delay to allow content to render
    setTimeout(() => {
      this.container.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }, 100)
  }

  /**
   * Re-run hash handling (useful after page transitions)
   */
  refresh() {
    this.handleHash()
  }
}
